import {
  Apollo_gql,
  GraphMutationBuilderPayload,
  GraphPayload,
  createMutation,
  graphQlUpdateFields,
} from '@camelot/server';

import { IEditabledDataUpdateFields } from '../models/types';
import { InsertPosition } from './dto/insert-position';

/** Decrit une grille editable du back : `grid<Entity>Rows`, `add<Entity>GridRow`, etc. */
export interface CamGridGraphConfig {
  entity: string;
  idArg: string;
  rowFields: () => string;
}

const affectedRows = (fields: string) => `affectedRows { row { ${fields} } modifiedProperties }`;

export function GET_GRID_ROWS(
  { entity, idArg, rowFields }: CamGridGraphConfig,
  entityId: string,
  showHidden?: boolean
): GraphPayload {
  const name = `grid${entity}Rows`;
  return {
    name,
    query: Apollo_gql`
      query Grid${entity}Rows($${idArg}: UUID!, $where: ${entity}RowGridFilterInput) {
        ${name}(${idArg}: $${idArg}, where: $where) { ${rowFields()} }
      }
    `,
    variables: showHidden ? { [idArg]: entityId } : { [idArg]: entityId, where: { hidden: { neq: true } } },
  };
}

export function GET_GRID_FOOTER({ entity, idArg }: CamGridGraphConfig, entityId: string): GraphPayload {
  const name = `grid${entity}Footer`;
  return {
    name,
    query: Apollo_gql`
      query Grid${entity}Footer($${idArg}: UUID!) {
        ${name}(${idArg}: $${idArg}) { key value }
      }
    `,
    variables: { [idArg]: entityId },
  };
}

export function ADD_GRID_ROW(
  { entity, rowFields }: CamGridGraphConfig,
  entityId: string,
  type: number,
  relatedId?: string,
  position?: InsertPosition
): GraphMutationBuilderPayload {
  const fields = rowFields();
  return createMutation(`add${entity}GridRow`, {
    props: `addedRow { ${fields} } ${affectedRows(fields)}`,
    type: 'AddGridRowPayloadInput!',
    value: { entityId, position, relatedId, type },
  });
}

export function UPDATE_GRID_ROWS<T>(
  { entity, rowFields }: CamGridGraphConfig,
  entityId: string,
  fields: IEditabledDataUpdateFields<T>[]
): GraphMutationBuilderPayload {
  return createMutation(`update${entity}GridRows`, {
    props: `${affectedRows(rowFields())} errors { rowId cell error }`,
    value: {
      entityId,
      rows: fields.map(({ id, ...changes }) => ({ ...changes, rowId: id, ...graphQlUpdateFields(changes) })),
    },
  });
}

export function DELETE_GRID_ROWS(
  { entity, rowFields }: CamGridGraphConfig,
  entityId: string,
  ids: string[]
): GraphMutationBuilderPayload {
  return createMutation(`delete${entity}GridRows`, {
    props: `deletedIds ${affectedRows(rowFields())}`,
    type: 'DeleteGridRowsPayloadInput!',
    value: { entityId, ids },
  });
}

export function CONVERT_GRID_LINE(
  { entity, rowFields }: CamGridGraphConfig,
  id: string,
  newType: number
): GraphMutationBuilderPayload {
  const fields = rowFields();
  return createMutation(`convert${entity}GridLine`, {
    props: `convertedRow { ${fields} } ${affectedRows(fields)} errors { rowId cell error }`,
    type: 'ConvertGridLinePayloadInput!',
    value: { id, newType },
  });
}

export function MOVE_GRID_ROW(
  { entity, rowFields }: CamGridGraphConfig,
  id: string,
  targetId: string,
  position: InsertPosition
): GraphMutationBuilderPayload {
  const fields = rowFields();
  return createMutation(`move${entity}GridRow`, {
    props: `movedRow { ${fields} } ${affectedRows(fields)}`,
    type: 'MoveGridRowPayloadInput!',
    value: { id, position, targetId },
  });
}

export function DUPLICATE_GRID_ROW(
  { entity, rowFields }: CamGridGraphConfig,
  sourceId: string,
  targetId: string,
  position: InsertPosition
): GraphMutationBuilderPayload {
  const fields = rowFields();
  return createMutation(`duplicate${entity}GridRow`, {
    props: `addedRows { ${fields} } ${affectedRows(fields)}`,
    type: 'DuplicateGridRowPayloadInput!',
    value: { position, sourceId, targetId },
  });
}
